import React from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Chip
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';

const Certifications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguageContext();
  const t = translations[language];

  const CertificationCard = ({ certification, isLast }) => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Typography variant="h6" component="h3" color="primary" gutterBottom>
        {certification.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <BusinessIcon 
          sx={{ 
            mr: 1, 
            color: 'primary.main',
            width: { xs: '1.5rem', sm: '2rem' },
            height: { xs: '1.5rem', sm: '2rem' },
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'rotate(10deg) scale(1.1)',
            },
          }} 
        />
        <Typography variant="subtitle1" color="text.secondary">
          {certification.issuer}
        </Typography>
      </Box>

      <Chip
        label={certification.period}
        color="primary"
        variant="outlined"
        sx={{
          mb: 2,
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
      />

      {certification.id && (
        <Typography variant="body2" color="text.secondary">
          ID: {certification.id}
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box sx={{ py: 6 }}>
      <Box 
        sx={{ 
          mb: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            fontWeight: 'bold'
          }}
        >
          {isMobile && (
            <WorkspacePremiumIcon 
              sx={{ 
                color: 'primary.main',
                width: { xs: '1.5rem', sm: '2rem' },
                height: { xs: '1.5rem', sm: '2rem' },
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'rotate(10deg) scale(1.1)',
                },
              }} 
            />
          )}
          {t.about.certifications.title}
        </Typography>
      </Box>

      {isMobile ? (
        <Stack spacing={3}>
          {t.about.certifications.items.map((certification, index) => (
            <CertificationCard 
              key={index} 
              certification={certification}
              isLast={index === t.about.certifications.items.length - 1}
            />
          ))}
        </Stack>
      ) : (
        <Timeline position="alternate">
          {t.about.certifications.items.map((certification, index) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    bgcolor: 'primary.main',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.2) rotate(10deg)',
                    },
                  }}
                >
                  <WorkspacePremiumIcon sx={{ fontSize: '1.2rem', color: 'white' }} />
                </TimelineDot>
                {index < t.about.certifications.items.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <CertificationCard certification={certification} />
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
};

export default Certifications;
